import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Field } from "formik";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Style/CompanySettings.css";
import { path_url } from "../../Config/config";

const CURRENCY_OPTIONS = [
  "USD", "EUR", "GBP", "INR", "AUD", "CAD", "JPY", "CNY", "CHF", "AED",
  "SGD", "NZD", "HKD", "NOK", "SEK", "DKK", "MXN", "BRL", "ZAR", "RUB",
];

const defaultSchema = Yup.object().shape({
  invoiceFormat: Yup.string().required("Required"),
});

function FormLabel({ text, required }) {
  return (
    <label className="cs-label">
      {text}
      {required && <sup>*</sup>}
    </label>
  );
}

export default function DefaultValue() {
  const [dbValues, setDbValues] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(
    localStorage.getItem("invoiceLogoPreview") || "",
  );
  const fileRef = useRef(null);

  useEffect(() => {
    const fetchDefaults = async () => {
      const ls = (k) => localStorage.getItem(k) || "";
      const token = ls("accessToken");
      const adminId = ls("adminId");

      const fromStorage = {
        invoiceFormat: ls("defaultInvoiceFormat"),
        invoiceDueDayPeriod: ls("defaultInvoiceDueDayPeriod"),
        taskDueDayPeriod: ls("defaultTaskDueDayPeriod"),
        serviceRequestDueDayPeriod: ls("defaultServiceRequestDueDayPeriod"),
        interactionRecordDueDayPeriod: ls(
          "defaultInteractionRecordDueDayPeriod",
        ),
        purchaseOrderDueDayPeriod: ls("defaultPurchaseOrderDueDayPeriod"),
        quotationDueDayPeriod: ls("defaultQuotationDueDayPeriod"),
        currency: ls("defaultCurrency"),
      };
      setDbValues(fromStorage);

      if (!token || !adminId) return;
      try {
        const { data } = await axios.get(
          `${path_url}/admindefaultvalues/${adminId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const d = data?.body || data || {};
        const loaded = {
          invoiceFormat: d.invoiceFormat || fromStorage.invoiceFormat,
          invoiceDueDayPeriod:
            d.invoiceDueDayPeriod || fromStorage.invoiceDueDayPeriod,
          taskDueDayPeriod: d.taskDueDayPeriod || fromStorage.taskDueDayPeriod,
          serviceRequestDueDayPeriod:
            d.serviceRequestDueDayPeriod ||
            fromStorage.serviceRequestDueDayPeriod,
          interactionRecordDueDayPeriod:
            d.interactionRecordDueDayPeriod ||
            fromStorage.interactionRecordDueDayPeriod,
          purchaseOrderDueDayPeriod:
            d.purchaseOrderDueDayPeriod ||
            fromStorage.purchaseOrderDueDayPeriod,
          quotationDueDayPeriod:
            d.quotationDueDayPeriod || fromStorage.quotationDueDayPeriod,
          currency: d.currency || fromStorage.currency,
        };
        setDbValues(loaded);
        Object.entries(loaded).forEach(([k, v]) =>
          localStorage.setItem(
            `default${k.charAt(0).toUpperCase() + k.slice(1)}`,
            v,
          ),
        );
      } catch (e) {
        console.warn("GET /admindefaultvalues failed:", e?.response?.status);
      }
    };
    fetchDefaults();
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be < 5 MB");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      localStorage.setItem("invoiceLogoPreview", ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <Formik
        initialValues={
          dbValues || {
            invoiceFormat: localStorage.getItem("defaultInvoiceFormat") || "",
            invoiceDueDayPeriod:
              localStorage.getItem("defaultInvoiceDueDayPeriod") || "",
            taskDueDayPeriod:
              localStorage.getItem("defaultTaskDueDayPeriod") || "",
            serviceRequestDueDayPeriod:
              localStorage.getItem("defaultServiceRequestDueDayPeriod") || "",
            interactionRecordDueDayPeriod:
              localStorage.getItem("defaultInteractionRecordDueDayPeriod") ||
              "",
            purchaseOrderDueDayPeriod:
              localStorage.getItem("defaultPurchaseOrderDueDayPeriod") || "",
            quotationDueDayPeriod:
              localStorage.getItem("defaultQuotationDueDayPeriod") || "",
            currency: localStorage.getItem("defaultCurrency") || "",
          }
        }
        enableReinitialize={true}
        validationSchema={defaultSchema}
        onSubmit={async (values) => {
          try {
            const token = localStorage.getItem("accessToken");
            const adminId = localStorage.getItem("adminId");
            if (!token || !adminId) {
              toast.error("Authentication missing.");
              return;
            }

            const payload = {
              useradminid: adminId,
              usercid: adminId,
              invoiceFormat: values.invoiceFormat,
              invoiceDueDayPeriod: values.invoiceDueDayPeriod,
              taskDueDayPeriod: values.taskDueDayPeriod,
              serviceRequestDueDayPeriod: values.serviceRequestDueDayPeriod,
              interactionRecordDueDayPeriod:
                values.interactionRecordDueDayPeriod,
              purchaseOrderDueDayPeriod: values.purchaseOrderDueDayPeriod,
              quotationDueDayPeriod: values.quotationDueDayPeriod,
              currency: values.currency,
            };

            const authHeader = {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            };
            const res = await axios.post(
              `${path_url}/admindefaultvalues/admin/defaultvalue`,
              payload,
              { headers: authHeader },
            );

            if (logoFile) {
              try {
                const fd = new FormData();
                fd.append("file", logoFile);
                fd.append("adminId", adminId);
                await axios.post(
                  `${path_url}/admindefaultvalues/invoice-logo`,
                  fd,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "multipart/form-data",
                    },
                  },
                );
              } catch (logoErr) {
                console.warn("Logo upload failed:", logoErr?.response?.status);
              }
            }

            const ok =
              res?.status === 200 || res?.data?.statusCodeValue === 200;
            localStorage.setItem("defaultInvoiceFormat", values.invoiceFormat);
            localStorage.setItem(
              "defaultInvoiceDueDayPeriod",
              values.invoiceDueDayPeriod,
            );
            localStorage.setItem(
              "defaultTaskDueDayPeriod",
              values.taskDueDayPeriod,
            );
            localStorage.setItem(
              "defaultServiceRequestDueDayPeriod",
              values.serviceRequestDueDayPeriod,
            );
            localStorage.setItem(
              "defaultInteractionRecordDueDayPeriod",
              values.interactionRecordDueDayPeriod,
            );
            localStorage.setItem(
              "defaultPurchaseOrderDueDayPeriod",
              values.purchaseOrderDueDayPeriod,
            );
            localStorage.setItem(
              "defaultQuotationDueDayPeriod",
              values.quotationDueDayPeriod,
            );
            localStorage.setItem("defaultCurrency", values.currency);

            if (ok) {
              setLogoFile(null);
              toast.success("Default values saved successfully!");
            } else {
              toast.error(res?.data?.body || "Failed to save.");
            }
          } catch (error) {
            localStorage.setItem("defaultInvoiceFormat", values.invoiceFormat);
            localStorage.setItem(
              "defaultInvoiceDueDayPeriod",
              values.invoiceDueDayPeriod,
            );
            localStorage.setItem(
              "defaultTaskDueDayPeriod",
              values.taskDueDayPeriod,
            );
            localStorage.setItem(
              "defaultServiceRequestDueDayPeriod",
              values.serviceRequestDueDayPeriod,
            );
            localStorage.setItem(
              "defaultInteractionRecordDueDayPeriod",
              values.interactionRecordDueDayPeriod,
            );
            localStorage.setItem(
              "defaultPurchaseOrderDueDayPeriod",
              values.purchaseOrderDueDayPeriod,
            );
            localStorage.setItem(
              "defaultQuotationDueDayPeriod",
              values.quotationDueDayPeriod,
            );
            localStorage.setItem("defaultCurrency", values.currency);
            toast.warning(
              "Saved locally. Backend error: " +
                (error?.response?.data?.message ||
                  error?.response?.data ||
                  error.message),
            );
          }
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <fieldset>
              <div className="cs-section-header">
                <p>Default Values</p>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="cs-form-row">
                    <FormLabel text="Invoice Format" required />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Enter invoice format"
                        name="invoiceFormat"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.invoiceFormat}
                      />
                      {formik.touched.invoiceFormat &&
                        formik.errors.invoiceFormat && (
                          <small className="cs-error">
                            {formik.errors.invoiceFormat}
                          </small>
                        )}
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Invoice Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="invoiceDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.invoiceDueDayPeriod}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Task Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="taskDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.taskDueDayPeriod}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Service Request Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="serviceRequestDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.serviceRequestDueDayPeriod}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="cs-form-row">
                    <FormLabel text="Interaction Record Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="interactionRecordDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.interactionRecordDueDayPeriod}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Purchase Order Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="purchaseOrderDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.purchaseOrderDueDayPeriod}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Quotation Due Day Period" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="number"
                        min="0"
                        placeholder="Enter days"
                        name="quotationDueDayPeriod"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.quotationDueDayPeriod}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Currency" />
                    <div className="cs-field-wrap">
                      <Field
                        as="select"
                        name="currency"
                        id="currency"
                        className="cs-select"
                      >
                        <option value="">Select Currency</option>
                        {CURRENCY_OPTIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Upload Invoice Logo" />
                    <div className="cs-field-wrap">
                      {logoPreview && (
                        <img
                          src={logoPreview}
                          alt="Invoice logo preview"
                          className="cs-logo-preview"
                        />
                      )}
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleLogoChange}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => fileRef.current.click()}
                      >
                        {logoPreview ? "Change Logo" : "Choose File"}
                      </button>
                      {logoFile && (
                        <small className="ml-2 text-muted">
                          {logoFile.name}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="cs-btn-row">
                    <button
                      type="button"
                      className="cs-btn-cancel"
                      onClick={() => {
                        formik.resetForm();
                        setLogoFile(null);
                        setLogoPreview(
                          localStorage.getItem("invoiceLogoPreview") || "",
                        );
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="cs-btn-save">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        )}
      </Formik>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}